import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  TextInput,
  Table,
  Avatar,
  Group,
  Text,
  Badge,
  Select,
  Stack,
  Loader,
  Center,
  Alert,
  Pagination,
  Paper,
  Menu,
  ActionIcon,
  Modal,
  Textarea,
  Switch,
  Button,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconSearch,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconLock,
  IconUser,
  IconBan,
  IconTrash,
  IconDots,
} from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { adminService } from '../../services/admin';
import { UserRole } from '../../types';
import { format } from 'date-fns';

interface User {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  profileImageUrl?: string;
  isEmailVerified: boolean;
  isPrivate: boolean;
  reviewCount: number;
  followerCount: number;
  reportsCount: number;
  createdAt: string;
  isBanned: boolean;
  banReason?: string;
  bannedAt?: string;
  bannedUntil?: string;
}

const roleOptions = [
  { value: '0', label: 'User' },
  { value: '1', label: 'Moderator' },
  { value: '2', label: 'Admin' },
];

const getRoleName = (role: UserRole) => {
  switch (role) {
    case UserRole.Admin: return 'Admin';
    case UserRole.Moderator: return 'Moderator';
    case UserRole.User: return 'User';
    default: return 'Unknown';
  }
};

const getRoleColor = (role: UserRole) => {
  switch (role) {
    case UserRole.Admin: return 'red';
    case UserRole.Moderator: return 'orange';
    case UserRole.User: return 'blue';
    default: return 'gray';
  }
};

export default function Users() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [banModalUser, setBanModalUser] = useState<User | null>(null);
  const [banReason, setBanReason] = useState('');
  const [isTempBan, setIsTempBan] = useState(false);
  const [banDuration, setBanDuration] = useState('7');
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', page, debouncedSearch],
    queryFn: () => adminService.getUsers(page, 20, debouncedSearch || undefined),
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: number }) =>
      adminService.updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      notifications.show({
        title: 'Role Updated',
        message: 'User role has been updated successfully',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update user role',
        color: 'red',
      });
    },
  });

  const banMutation = useMutation({
    mutationFn: ({ userId, reason, bannedUntil }: { userId: string; reason: string; bannedUntil?: string }) =>
      adminService.banUser(userId, { reason, bannedUntil }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      notifications.show({
        title: 'User Banned',
        message: 'User has been banned successfully',
        color: 'orange',
        icon: <IconBan size={16} />,
      });
      setBanModalUser(null);
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to ban user',
        color: 'red',
      });
    },
  });

  const unbanMutation = useMutation({
    mutationFn: (userId: string) => adminService.unbanUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      notifications.show({
        title: 'User Unbanned',
        message: 'User has been unbanned successfully',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to unban user',
        color: 'red',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => adminService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      notifications.show({
        title: 'User Deleted',
        message: 'User account has been permanently deleted',
        color: 'red',
        icon: <IconTrash size={16} />,
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete user',
        color: 'red',
      });
    },
  });

  const handleRoleChange = (userId: string, username: string, newRole: string) => {
    modals.openConfirmModal({
      title: 'Change User Role',
      children: (
        <Text size="sm">
          Are you sure you want to change <strong>@{username}</strong>'s role to{' '}
          <strong>{roleOptions.find((r) => r.value === newRole)?.label}</strong>?
        </Text>
      ),
      labels: { confirm: 'Confirm', cancel: 'Cancel' },
      onConfirm: () => {
        updateRoleMutation.mutate({ userId, role: parseInt(newRole) });
      },
    });
  };

  const handleBanUser = (user: User) => {
    setBanModalUser(user);
    setBanReason('');
    setIsTempBan(false);
    setBanDuration('7');
  };

  const handleBanConfirm = () => {
    if (!banModalUser || !banReason.trim()) return;

    const bannedUntil = isTempBan
      ? new Date(Date.now() + parseInt(banDuration) * 24 * 60 * 60 * 1000).toISOString()
      : undefined;

    banMutation.mutate({
      userId: banModalUser.id,
      reason: banReason,
      bannedUntil,
    });
  };

  const handleUnbanUser = (userId: string, username: string) => {
    modals.openConfirmModal({
      title: 'Unban User',
      children: (
        <Text size="sm">
          Are you sure you want to unban <strong>@{username}</strong>?
        </Text>
      ),
      labels: { confirm: 'Unban', cancel: 'Cancel' },
      confirmProps: { color: 'green' },
      onConfirm: () => unbanMutation.mutate(userId),
    });
  };

  const handleDeleteUser = (userId: string, username: string) => {
    modals.openConfirmModal({
      title: 'Delete User Account',
      children: (
        <Text size="sm">
          Are you sure you want to <strong>permanently delete</strong> @{username}'s account?
          This will remove all their reviews, comments, likes, and followers. This cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete Account', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteMutation.mutate(userId),
    });
  };

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        Failed to load user list. Please try again later.
      </Alert>
    );
  }

  const rows = data?.items.map((user: User) => (
    <Table.Tr key={user.id}>
      <Table.Td>
        <Group gap="sm">
          <Avatar src={user.profileImageUrl} radius="xl" size="md">
            <IconUser size={20} />
          </Avatar>
          <div>
            <Text size="sm" fw={500}>
              {user.username}
            </Text>
            <Group gap={4}>
              {user.isEmailVerified ? (
                <Badge size="xs" variant="light" color="green" leftSection={<IconCheck size={10} />}>
                  Verified
                </Badge>
              ) : (
                <Badge size="xs" variant="light" color="gray" leftSection={<IconX size={10} />}>
                  Not verified
                </Badge>
              )}
              {user.isPrivate && (
                <Badge size="xs" variant="light" color="gray" leftSection={<IconLock size={10} />}>
                  Private
                </Badge>
              )}
              {user.isBanned && (
                <Tooltip label={user.banReason || 'Banned'}>
                  <Badge size="xs" variant="light" color="red" leftSection={<IconBan size={10} />}>
                    {user.bannedUntil
                      ? `Suspended until ${format(new Date(user.bannedUntil), 'MMM d')}`
                      : 'Banned'}
                  </Badge>
                </Tooltip>
              )}
            </Group>
          </div>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={1}>
          {user.email}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge color={getRoleColor(user.role)} variant="light">
          {getRoleName(user.role)}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Stack gap={2}>
          <Text size="xs">{user.reviewCount} reviews</Text>
          <Text size="xs">{user.followerCount} followers</Text>
          {user.reportsCount > 0 && (
            <Text size="xs" c="red" fw={500}>
              {user.reportsCount} reports
            </Text>
          )}
        </Stack>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {format(new Date(user.createdAt), 'MMM d, yyyy')}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Select
            size="xs"
            value={String(user.role)}
            onChange={(value) => value && handleRoleChange(user.id, user.username, value)}
            data={roleOptions}
            disabled={updateRoleMutation.isPending}
            w={110}
          />
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {user.isBanned ? (
                <Menu.Item
                  leftSection={<IconCheck size={14} />}
                  color="green"
                  onClick={() => handleUnbanUser(user.id, user.username)}
                >
                  Unban User
                </Menu.Item>
              ) : (
                <Menu.Item
                  leftSection={<IconBan size={14} />}
                  color="orange"
                  onClick={() => handleBanUser(user)}
                  disabled={user.role === UserRole.Admin || user.role === UserRole.Moderator}
                >
                  Ban User
                </Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => handleDeleteUser(user.id, user.username)}
                disabled={user.role === UserRole.Admin || user.role === UserRole.Moderator}
              >
                Delete Account
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>User Management</Title>

        <TextInput
          placeholder="Search by username or email..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          w={400}
        />
      </Group>

      <Paper shadow="xs" radius="md" withBorder>
        {isLoading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>User</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Stats</Table.Th>
                <Table.Th>Joined</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        )}
      </Paper>

      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Showing {data?.items.length || 0} of {data?.totalCount || 0} users
        </Text>
        <Pagination
          value={page}
          onChange={setPage}
          total={data?.totalPages || 1}
        />
      </Group>

      {/* Ban User Modal */}
      <Modal
        opened={banModalUser !== null}
        onClose={() => setBanModalUser(null)}
        title={`Ban @${banModalUser?.username}`}
      >
        <Stack gap="md">
          <Textarea
            label="Ban Reason"
            placeholder="Describe why this user is being banned..."
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
            required
            minRows={3}
            maxLength={500}
          />
          <Switch
            label="Temporary suspension"
            checked={isTempBan}
            onChange={(e) => setIsTempBan(e.currentTarget.checked)}
          />
          {isTempBan && (
            <Select
              label="Duration"
              value={banDuration}
              onChange={(value) => value && setBanDuration(value)}
              data={[
                { value: '1', label: '1 day' },
                { value: '3', label: '3 days' },
                { value: '7', label: '7 days' },
                { value: '14', label: '14 days' },
                { value: '30', label: '30 days' },
                { value: '90', label: '90 days' },
              ]}
            />
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setBanModalUser(null)}>
              Cancel
            </Button>
            <Button
              color="orange"
              onClick={handleBanConfirm}
              disabled={!banReason.trim()}
              loading={banMutation.isPending}
            >
              {isTempBan ? 'Suspend User' : 'Ban User'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
